import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  message,
  Space,
  Popconfirm,
  Card,
  Row,
  Col,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import api from '../services/api';
import ShortId from './common/ShortId';

const TipoUnidadesList = () => {
  const [tipoUnidades, setTipoUnidades] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form] = Form.useForm();

  // Buscar lista de tipos de unidades
  const fetchTipoUnidades = async () => {
    setLoading(true);
    try {
      const response = await api.get('/api/tipo-unidades/');
      setTipoUnidades(response.data);
    } catch (error) {
      message.error('Erro ao carregar tipos de unidades');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTipoUnidades();
  }, []);

  // Abrir modal para criar novo
  const handleCreate = () => {
    setEditingId(null);
    form.resetFields();
    setIsModalVisible(true);
  };

  // Abrir modal para editar
  const handleEdit = (record) => {
    setEditingId(record.id);
    form.setFieldsValue({
      nome_unidade: record.nome_unidade,
    });
    setIsModalVisible(true);
  };

  // Salvar (criar ou atualizar)
  const handleSave = async (values) => {
    try {
      if (editingId) {
        // Atualizar
        await api.put(`/api/tipo-unidades/${editingId}`, values);
        message.success('Tipo de unidade atualizado com sucesso');
      } else {
        // Criar
        await api.post('/api/tipo-unidades/', values);
        message.success('Tipo de unidade criado com sucesso');
      }
      setIsModalVisible(false);
      form.resetFields();
      fetchTipoUnidades();
    } catch (error) {
      const errorMsg = error.response?.data?.detail 
        ? (typeof error.response.data.detail === 'string' 
          ? error.response.data.detail 
          : JSON.stringify(error.response.data.detail))
        : 'Erro ao salvar tipo de unidade';
      message.error(errorMsg);
      console.error(error);
    }
  };

  // Deletar
  const handleDelete = async (id) => {
    try {
      await api.delete(`/api/tipo-unidades/${id}`);
      message.success('Tipo de unidade deletado com sucesso');
      fetchTipoUnidades();
    } catch (error) {
      const errorMsg = error.response?.data?.detail 
        ? (typeof error.response.data.detail === 'string' 
          ? error.response.data.detail 
          : JSON.stringify(error.response.data.detail))
        : 'Erro ao deletar tipo de unidade';
      message.error(errorMsg);
      console.error(error);
    }
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
      render: (id) => <ShortId id={id} />,
    },
    {
      title: 'Nome da Unidade',
      dataIndex: 'nome_unidade',
      key: 'nome_unidade',
      sorter: (a, b) => a.nome_unidade.localeCompare(b.nome_unidade),
    },
    {
      title: 'Ações',
      key: 'actions',
      width: 150,
      render: (_, record) => (
        <Space>
          <Button
            type="primary"
            icon={<EditOutlined />}
            size="small"
            onClick={() => handleEdit(record)}
          >
            Editar
          </Button>
          <Popconfirm
            title="Confirmar exclusão"
            description="Tem certeza que deseja deletar este tipo de unidade?"
            onConfirm={() => handleDelete(record.id)}
            okText="Sim"
            cancelText="Não"
          >
            <Button
              type="primary"
              danger
              icon={<DeleteOutlined />}
              size="small"
            >
              Deletar
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Card
        title="Tipos de Unidades"
        extra={
          <Space>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleCreate}
            >
              Novo
            </Button>
            <Button
              icon={<ReloadOutlined />}
              onClick={fetchTipoUnidades}
              loading={loading}
            >
              Atualizar
            </Button>
          </Space>
        }
      >
        <Table
          columns={columns}
          dataSource={tipoUnidades}
          loading={loading}
          rowKey="id"
          pagination={{ pageSize: 10 }}
        />
      </Card>

      <Modal
        title={editingId ? 'Editar Tipo de Unidade' : 'Novo Tipo de Unidade'}
        visible={isModalVisible}
        onOk={() => form.submit()}
        onCancel={() => {
          setIsModalVisible(false);
          form.resetFields();
        }}
        okText={editingId ? 'Atualizar' : 'Criar'}
        cancelText="Cancelar"
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSave}
        >
          <Form.Item
            name="nome_unidade"
            label="Nome da Unidade"
            rules={[
              {
                required: true,
                message: 'Por favor, insira o nome da unidade',
              },
              {
                min: 1,
                max: 100,
                message: 'O nome deve ter entre 1 e 100 caracteres',
              },
            ]}
          >
            <Input placeholder="Ex: Escola, ONG, Instituto" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default TipoUnidadesList;
